import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Modal } from 'atoms';
import { PopupContainer } from 'molecules';
import { usePaymentMutations } from 'core/hooks';

import * as L from '../StudentDetail.styled';
import { IAgreement, IStudent } from './types';

interface IAddContributionInlinePopupProps {
  student: IStudent;
  agreement: IAgreement;
  studentId: string;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Inline popup for «+ Взнос в план»: a financier-driven way to extend the
 * agreement schedule with a single planned installment. The new
 * Contribution lands on the schedule as `⏳ Ожидается` and can then be
 * paid by the regular «+ Оплатить» flow.
 *
 * Why we don't talk to a dedicated `addContribution` mutation: the backend
 * doesn't have one (see §10.1 candidate). `editStudentWithAgreement`
 * rebuilds the whole schedule from the array we send, so we ship the
 * existing rows + the new one. Toast/refetch is handled by
 * `usePaymentMutations.addContribution`.
 */
export const AddContributionInlinePopup = memo(
  ({
    student,
    agreement,
    studentId,
    onClose,
    onSaved,
  }: IAddContributionInlinePopupProps) => {
    const { t } = useTranslation();
    const { addContribution, loading } = usePaymentMutations(studentId);

    const today = useMemo(() => moment().format('YYYY-MM-DD'), []);
    const [date, setDate] = useState<string>(today);
    const [amount, setAmount] = useState<string>('');

    const currency = agreement.currency || '';

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        const num = parseFloat(amount.replace(',', '.'));
        if (!num || num <= 0 || !date) return;
        const ok = await addContribution({
          student,
          agreement,
          date,
          amountForeign: num,
        });
        if (ok) {
          onSaved?.();
          onClose();
        }
      },
      [amount, date, addContribution, student, agreement, onSaved, onClose],
    );

    return (
      <Modal>
        <PopupContainer
          width="480px"
          title={t('pages.studentDetail.payments.addContribution.title')}
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              <L.InlineNotice
                style={{
                  background: '#f0f9ff',
                  borderColor: '#bae6fd',
                  color: '#075985',
                }}
              >
                {t('pages.studentDetail.payments.addContribution.hint', {
                  agreement: agreement.agreementTitle,
                  currency: currency || '—',
                })}
              </L.InlineNotice>

              <L.FormLabel>
                {t('pages.studentDetail.payments.addContribution.dateLabel')}
                <L.FormInput
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  autoFocus
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.studentDetail.payments.addContribution.amountLabel', {
                  currency: currency || '—',
                })}
                <L.FormInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </L.FormLabel>

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t('pages.studentDetail.payments.form.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={loading || !amount || !date}
                >
                  {loading
                    ? t('pages.studentDetail.payments.form.saving')
                    : t('pages.studentDetail.payments.addContribution.submit')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
